"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.canViewBirthdays = canViewBirthdays;
exports.default = BirthdaysCard;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const cancelable_promise_1 = require("cancelable-promise");
const react_i18next_1 = require("react-i18next");
const lottie_react_native_1 = __importDefault(require("lottie-react-native"));
const Card_1 = __importDefault(require("../components/Card"));
const Loading_1 = __importDefault(require("../components/Loading"));
const UserContext_1 = require("../contexts/UserContext");
const ThemeContext_1 = require("../ThemeContext");
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
const Employees_1 = __importDefault(require("../api/Employees"));
const CompanySettings_1 = __importDefault(require("../api/CompanySettings"));
const Helper_1 = require("../Helper");
const moment_1 = __importDefault(require("moment"));
const balloons_svg_1 = __importDefault(require("../images/balloons.svg"));
const cake_svg_1 = __importDefault(require("../images/cake.svg"));
const present_svg_1 = __importDefault(require("../images/present.svg"));
const partypopper_svg_1 = __importDefault(require("../images/partypopper.svg"));
const parseBirthdayDate = (dateInput, todayYear, todayUtcMs) => {
    if (!dateInput || !todayYear || todayUtcMs === undefined)
        return null;
    let rawMonth = -1;
    let rawDay = -1;
    if (dateInput instanceof Date) {
        if (isNaN(dateInput.getTime()))
            return null;
        rawMonth = dateInput.getUTCMonth();
        rawDay = dateInput.getUTCDate();
    }
    else {
        const dateStr = String(dateInput);
        // Check for YYYY-MM-DD
        const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
        if (isoMatch) {
            rawMonth = parseInt(isoMatch[2], 10) - 1;
            rawDay = parseInt(isoMatch[3], 10);
        }
        else {
            // Check for MM/DD/YYYY
            const usMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})/);
            if (usMatch) {
                rawMonth = parseInt(usMatch[1], 10) - 1;
                rawDay = parseInt(usMatch[2], 10);
            }
            else {
                const parsed = new Date(dateStr);
                if (isNaN(parsed.getTime()))
                    return null;
                rawMonth = parsed.getUTCMonth();
                rawDay = parsed.getUTCDate();
            }
        }
    }
    if (rawMonth < 0 || rawMonth > 11 || rawDay < 1 || rawDay > 31)
        return null;
    let bdayUtcMs = Date.UTC(todayYear, rawMonth, rawDay);
    if (bdayUtcMs < todayUtcMs) {
        // Check if next year's occurrence falls within the upcoming 7-day window
        const nextYearMs = Date.UTC(todayYear + 1, rawMonth, rawDay);
        if (nextYearMs - todayUtcMs < 7 * 86400000) {
            bdayUtcMs = nextYearMs;
        }
    }
    const diffDays = Math.round((bdayUtcMs - todayUtcMs) / 86400000);
    return {
        bdayDate: new Date(bdayUtcMs),
        diffDays,
        isToday: diffDays === 0
    };
};
function canViewBirthdays(userContext) {
    const clientRole = userContext?.employee?.clients?.[userContext?.client?.id]?.role ??
        userContext?.user?.claims?.clients?.[userContext?.client?.id] ??
        '';
    const isFormer = userContext?.isFormer || clientRole.includes('F');
    const isContractor = userContext?.isContractor || clientRole.includes('C');
    const isAdmin = userContext?.isAdmin || clientRole.includes('A');
    const isApprover = userContext?.isApprover || clientRole.includes('P');
    const isActiveEmployee = !isFormer && !isContractor && (clientRole.includes('E') || (!isAdmin && !isApprover));
    return !isFormer && !isContractor && (isActiveEmployee || isAdmin || isApprover);
}
function BirthdaysCard(props) {
    const { t } = (0, react_i18next_1.useTranslation)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    const { columns } = (0, ResponsiveContext_1.useResponsiveContext)();
    const canView = canViewBirthdays(userContext);
    const promise = react_1.default.useRef(null);
    const confettiRef = react_1.default.useRef(null);
    const [status, setStatus] = react_1.default.useState('loading');
    const [birthdays, setBirthdays] = react_1.default.useState([]);
    const [hasBirthdayToday, setHasBirthdayToday] = react_1.default.useState(false);
    const [confettiFinished, setConfettiFinished] = react_1.default.useState(false);
    const [visible, setVisible] = react_1.default.useState(typeof props.visible === 'boolean' ? props.visible : null);
    react_1.default.useEffect(() => {
        if (typeof props.visible === 'boolean') {
            setVisible(props.visible);
        }
    }, [props.visible]);
    const refresh = react_1.default.useCallback(() => {
        if (!canView)
            return;
        promise.current?.cancel();
        setStatus('loading');
        const now = new Date();
        const todayYear = now.getFullYear();
        const todayUtcMs = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
        const startDate = (0, moment_1.default)().format('YYYY-MM-DD');
        const endDate = (0, moment_1.default)().add(6, 'days').format('YYYY-MM-DD');
        promise.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(async (token) => {
            let isVisible = props.visible;
            if (typeof isVisible !== 'boolean') {
                const setting = await CompanySettings_1.default.getBirthdayCard(token).catch(() => ({ visible: true }));
                isVisible = setting?.visible ?? true;
            }
            if (!isVisible) {
                return { isVisible: false, birthdaysData: null };
            }
            const birthdaysData = await Employees_1.default.getBirthdays(token, startDate, endDate);
            return { isVisible: true, birthdaysData };
        })).then(({ isVisible, birthdaysData }) => {
            setVisible(isVisible);
            if (!isVisible) {
                setBirthdays([]);
                setHasBirthdayToday(false);
                setStatus('done');
                return;
            }
            const list = birthdaysData?.list ?? [];
            const upcomingBirthdays = [];
            let todayFound = false;
            list.forEach((item) => {
                if (!item.type || item.type.toLowerCase() === 'birthday') {
                    const parsed = parseBirthdayDate(item.normalizedDate || item.actualDate, todayYear, todayUtcMs);
                    if (parsed && parsed.diffDays >= 0 && parsed.diffDays < 7) {
                        if (parsed.isToday) {
                            todayFound = true;
                        }
                        upcomingBirthdays.push({
                            ...item,
                            bdayDate: parsed.bdayDate,
                            diffDays: parsed.diffDays,
                            isToday: parsed.isToday
                        });
                    }
                }
            });
            upcomingBirthdays.sort((a, b) => {
                if (a.diffDays !== b.diffDays) {
                    return a.diffDays - b.diffDays;
                }
                return (a.fullName ?? '').localeCompare(b.fullName ?? '');
            });
            setBirthdays(upcomingBirthdays);
            setHasBirthdayToday(todayFound);
            setStatus('done');
        }).catch((e) => {
            setBirthdays([]);
            setHasBirthdayToday(false);
            setStatus(e === 404 ? 'done' : 'error');
        });
    }, [userContext, canView, props.visible]);
    react_1.default.useEffect(() => {
        if (canView) {
            refresh();
        }
        return () => {
            promise.current?.cancel();
        };
    }, [userContext?.client?.id, canView]);
    react_1.default.useEffect(() => {
        const sub = react_native_1.DeviceEventEmitter.addListener('event.birthdayCardSettingUpdated', (newVisible) => {
            setVisible(newVisible);
            if (newVisible) {
                refresh();
            }
        });
        return () => sub.remove();
    }, [refresh]);
    const numColumns = columns > 1 ? 2 : 1;
    const birthdayRows = react_1.default.useMemo(() => {
        const rows = [];
        for (let i = 0; i < birthdays.length; i += 2) {
            rows.push([birthdays[i], birthdays[i + 1]]);
        }
        return rows;
    }, [birthdays]);
    const renderBirthdayRow = (item, index) => ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', alignItems: 'center', gap: 14 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: {
                    borderTopColor: THEME.Red,
                    borderTopWidth: 5,
                    borderTopLeftRadius: 3,
                    borderTopRightRadius: 3
                }, children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: {
                        alignItems: 'center',
                        paddingVertical: 4,
                        borderColor: THEME.CardBorder,
                        borderWidth: 1,
                        borderTopWidth: 0,
                        borderBottomLeftRadius: 3,
                        borderBottomRightRadius: 3,
                        minWidth: 50,
                        backgroundColor: THEME.CardBackground
                    }, children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: {
                                color: THEME.Text,
                                fontSize: 11,
                                fontWeight: item.isToday ? '600' : '400',
                                textTransform: 'uppercase'
                            }, children: item.bdayDate.toLocaleDateString(Helper_1.Helper.deviceLanguage(), {
                                month: 'short',
                                timeZone: 'UTC'
                            }) }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: {
                                color: THEME.Text,
                                fontSize: 17,
                                fontWeight: item.isToday ? '700' : '500'
                            }, children: item.bdayDate.toLocaleDateString(Helper_1.Helper.deviceLanguage(), {
                                day: 'numeric',
                                timeZone: 'UTC'
                            }) })] }) }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 }, children: [(0, jsx_runtime_1.jsx)(cake_svg_1.default, { width: 18, height: 18, style: { flexShrink: 0 } }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flex: 1, flexDirection: 'column', justifyContent: 'center', gap: 3 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: {
                                    color: THEME.Text,
                                    fontSize: 16,
                                    fontWeight: item.isToday ? '600' : '400'
                                }, numberOfLines: 1, children: item.fullName }), item.isToday && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: {
                                    alignSelf: 'flex-start',
                                    backgroundColor: THEME.PrimaryLightest,
                                    borderRadius: 12,
                                    paddingHorizontal: 8,
                                    paddingVertical: 2,
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 4
                                }, children: [(0, jsx_runtime_1.jsx)(partypopper_svg_1.default, { width: 12, height: 12 }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Primary, fontSize: 11, fontWeight: '600' }, children: t('company:text.today', 'Today!') })] }))] })] })] }, item.employeeId ? `bday-${item.employeeId}-${index}` : `bday-${index}`));
    if (!canView || visible === false || (visible === null && status === 'loading')) {
        return null;
    }
    const cardContent = ((0, jsx_runtime_1.jsxs)(Card_1.default, { style: [
            {
                paddingBottom: 0,
                ...(columns === 1 ? {
                    backgroundColor: THEME.White,
                    marginHorizontal: -12,
                    paddingHorizontal: 12,
                    borderRadius: 0,
                    borderWidth: 0,
                    borderBottomWidth: 0,
                    marginTop: 0,
                    marginBottom: 0,
                } : {})
            },
            props.style
        ], children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: {
                    position: 'absolute',
                    bottom: 0,
                    right: 6,
                    zIndex: 0,
                    flexDirection: 'row',
                    alignItems: 'flex-end',
                    gap: 4,
                    opacity: 0.5
                }, pointerEvents: "none", children: [(0, jsx_runtime_1.jsx)(present_svg_1.default, { width: 27, height: 27 }), (0, jsx_runtime_1.jsx)(present_svg_1.default, { width: 54, height: 54 })] }), hasBirthdayToday && !confettiFinished && ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: [react_native_1.StyleSheet.absoluteFill, { zIndex: 0, overflow: 'hidden' }], pointerEvents: "none", children: (0, jsx_runtime_1.jsx)(lottie_react_native_1.default, { ref: confettiRef, source: require('../animations/confetti.json'), autoPlay: true, loop: false, style: { width: '100%', height: '100%' }, resizeMode: "cover", onAnimationFinish: () => setConfettiFinished(true) }) })), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { zIndex: 1, position: 'relative' }, children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }, children: [(0, jsx_runtime_1.jsx)(balloons_svg_1.default, { width: 22, height: 22, style: { flexShrink: 0 } }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { "aria-label": t('company:text.birthdaysToCelebrateThisWeek'), style: {
                                    fontSize: 18,
                                    fontWeight: '500',
                                    color: THEME.Text,
                                    textAlign: 'center',
                                    flexShrink: 1,
                                }, children: t('company:text.birthdaysToCelebrateThisWeek') }), (0, jsx_runtime_1.jsx)(balloons_svg_1.default, { width: 22, height: 22, style: { flexShrink: 0 } })] }), status === 'done' && ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { marginTop: 16, marginBottom: 68 }, children: birthdays.length === 0 ? ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { gap: 12, alignItems: 'center', justifyContent: 'center', marginVertical: 16 }, children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', gap: 16, alignItems: 'center', opacity: 0.6 }, children: [(0, jsx_runtime_1.jsx)(cake_svg_1.default, { width: 36, height: 36 }), (0, jsx_runtime_1.jsx)(present_svg_1.default, { width: 36, height: 36 })] }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { textAlign: 'center', fontSize: 15, color: THEME.TextNull, paddingHorizontal: 16 }, children: t('company:text.noUpcomingBirthdaysWeekAhead') })] })) : (numColumns === 1 ? ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { gap: 14 }, children: birthdays.map((item, index) => renderBirthdayRow(item, index)) })) : ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { gap: 14 }, children: birthdayRows.map(([leftItem, rightItem], rowIndex) => ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', gap: 20 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flex: 1 }, children: renderBirthdayRow(leftItem, rowIndex * 2) }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flex: 1 }, children: rightItem ? renderBirthdayRow(rightItem, rowIndex * 2 + 1) : null })] }, `bday-row-${rowIndex}`))) }))) })), status !== 'done' && ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { marginBottom: 16 }, children: (0, jsx_runtime_1.jsx)(Loading_1.default, { retryable: true, loading: status === 'loading', error: status === 'error', onRetry: refresh }) }))] })] }));
    if (columns > 1) {
        return cardContent;
    }
    return ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { marginBottom: 0 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: {
                    height: 14,
                    backgroundColor: THEME.ScreenBackground,
                    borderTopWidth: 1,
                    borderBottomWidth: 1,
                    borderColor: THEME.LightGray,
                    marginHorizontal: -12,
                } }), cardContent] }));
}
